<template>
    <!-- 2. Input/Output Topics -->
    <Card class="transition-opacity">
        <CardHeader>
            <CardTitle class="text-xl"
                ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">2</Badge>Input / Output Topics</CardTitle
            >
            <CardDescription>
                Define patterns and output topics via the
                <a href="/topics" target="_blank" class="text-accent-foreground underline">topics page</a>.
            </CardDescription>
        </CardHeader>
        <CardContent>
            <div class="grid grid-cols-1 md:[grid-template-columns:1fr_1fr_auto] gap-4 w-full">
                <div class="w-full">
                    <Label class="mb-2 block">Input Topic Pattern</Label>
                    <Input
                        v-model="pattern"
                        :disabled="loadingSetInOutTopics"
                        :class="{ 'border-destructive focus-visible:ring-red-500': setInOutError || fetchInOutError }"
                        type="text"
                        placeholder="e.g. .*\\.test\\.some-topic-prefix.*" />
                </div>
                <div class="w-full">
                    <div class="flex items-center gap-2 mb-2">
                        <Label class="block">Output Topic Replacement</Label>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="ghost" size="icon" class="h-4 w-4 p-0">
                                    <Info class="h-3 w-3 text-muted-foreground" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Backreferences supported (e.g., $1, $2 for regex capture groups)</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    <Input
                        v-model="outputTopic"
                        :disabled="loadingSetInOutTopics"
                        :class="{ 'border-destructive focus-visible:ring-red-500': setInOutError }"
                        type="text"
                        placeholder="e.g. test.merged_orders" />
                </div>
                <div class="flex items-end space-x-3">
                    <LoadingButton @click="callSetInOutTopics" :is-loading="loadingSetInOutTopics" class="bg-primary hover:bg-primary">
                        <Settings class="w-4 h-4 mr-2" />
                        Save Topics Pattern / Replacement
                    </LoadingButton>
                </div>
            </div>
            <Alert v-if="setInOutResult" variant="default" class="mt-4 border-green-200 bg-green-50">
                <CheckCircle class="h-4 w-4 text-green-600" />
                <AlertDescription class="text-green-800">{{ setInOutResult }}</AlertDescription>
            </Alert>
            <div v-if="setInOutError" class="mt-2">
                <ErrorMessage :error="setInOutError" :to-sentry="true" context="transform-impl-set-topics" />
            </div>

            <!-- Topics Search & Table -->
            <div v-if="fetchInOutError" class="mt-2">
                <ErrorMessage :error="fetchInOutError" :to-sentry="true" context="transform-impl-fetch-topics" />
            </div>
            <div class="table-page-container mt-10 mb-5">
                <div class="table-page-body">
                    <div>
                        <ScrollArea class="w-full border shadow-md rounded-md" :class="(inOutTopics.length || 0) <= 10 ? 'h-auto' : 'h-40-dynamic'">
                            <Table>
                                <TableHeader class="sticky top-0 bg-background z-10">
                                    <TableRow class="bg-muted/50">
                                        <TableHead class="first:rounded-tl-md">Input Topic</TableHead>
                                        <TableHead class="last:rounded-tr-md">Expected Output Topic Name</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <!-- Skeleton rows when loading -->
                                    <template v-if="topicTableDataLoading">
                                        <TableRow v-for="n in 5" :key="`skeleton-${n}`">
                                            <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                            <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                        </TableRow>
                                    </template>
                                    <!-- Actual data rows -->
                                    <template v-else>
                                        <TableRow v-for="inOutTopic in inOutTopics" :key="inOutTopic[0]">
                                            <TableCell class="font-medium">
                                                <div class="flex flex-col items-start gap-1">
                                                    <div class="whitespace-normal break-words">
                                                        <Button
                                                            variant="link"
                                                            class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                            @click="() => navigateToTopic(inOutTopic[0])">
                                                            <span class="hover:text-primary truncate">
                                                                {{ inOutTopic[0].replace(/(source_|transform_)[^.]+\./, '') }}
                                                            </span>
                                                        </Button>
                                                    </div>
                                                    <small class="text-muted-foreground">ID: {{ inOutTopic[0] }}</small>
                                                </div>
                                            </TableCell>
                                            <TableCell class="font-medium">
                                                <div class="flex flex-col items-start gap-1">
                                                    <div class="whitespace-normal break-words">
                                                        {{ inOutTopic[1].replace(/(source_|transform_)[^.]+\./, '') }}
                                                    </div>
                                                    <small class="text-muted-foreground">ID: {{ inOutTopic[1] }}</small>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                        <TableEmpty v-if="!topicTableDataLoading && !inOutTopics.length" :colspan="2">
                                            <div class="flex flex-col items-center justify-center py-6">
                                                <p class="text-muted-foreground text-sm">No topics found for the given pattern.</p>
                                            </div>
                                        </TableEmpty>
                                    </template>
                                </TableBody>
                            </Table>
                        </ScrollArea>
                    </div>
                </div>
            </div>
        </CardContent>
    </Card>

    <!-- 3. Transform Logic -->
    <Card class="transition-opacity">
        <CardHeader>
            <CardTitle class="text-xl"
                ><Badge variant="secondary" class="w-8 h-8 rounded-full p-0 justify-center mr-2">3</Badge>Transform Logic</CardTitle
            >
            <CardDescription>Define Python functions to transform record values, keys, and topics</CardDescription>
        </CardHeader>
        <CardContent>
            <!-- Display validation errors -->
            <div v-if="saveError !== null" class="mb-4">
                <ErrorMessage
                    :error="saveError"
                    :to-sentry="true"
                    context="transform-python-save"
                    introduction="Failed to save transform implementation" />
            </div>

            <!-- Monaco Tabbed Editor -->
            <MonacoTabbedEditor
                v-if="editorTabs.length > 0"
                :tabs="editorTabs"
                :loading="editorLoading"
                :is-saving="isSaving"
                :template-map="templateMap"
                language="python"
                @save="handleTabSave"
                @content-change="handleContentChangeWithAutoSave"
                @load-template="handleLoadTemplate"
                @tab-change="handleTabChange"
                @close="closeEditor" />
        </CardContent>
    </Card>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useTransformEditor } from '@/composables/useTransformEditor';
import { MonacoTabbedEditor } from '@/components/ui/monaco-tabbed-editor';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Settings, CheckCircle, Info } from 'lucide-vue-next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import ErrorMessage from '@/components/ErrorMessage.vue';
// REMOVED: No longer need store/router imports - pure UI component

// Import Python template files
import commonTransformTemplate from '../transform_code/commonTransform.py?raw';
import valueTransformTemplate from '../transform_code/valueTransform.py?raw';
import keyTransformTemplate from '../transform_code/keyTransform.py?raw';
import topicTransformTemplate from '../transform_code/topicTransform.py?raw';
import valueSchemaTransformTemplate from '../transform_code/valueSchemaTransform.py?raw';
import keySchemaTransformTemplate from '../transform_code/keySchemaTransform.py?raw';

// Props - SIMPLIFIED: Pure UI component receives data only
const props = defineProps({
    transform: {
        type: Object,
        required: true
    },
    implementationCurrentVersion: {
        type: Object,
        required: true
    },
    hasUnsavedChanges: {
        type: Boolean,
        default: false
    },
    isSaving: {
        type: Boolean,
        default: false
    },
    saveError: {
        type: Object,
        default: null
    }
});

// Emits - SIMPLIFIED: Only emit content changes and navigation
const emit = defineEmits(['implementation-updated', 'show-example-clicked']);

import { useTopicPatternManager } from '@/composables/useTopicPatternManager';

// Import shared utility for tab creation
import { createScriptTransformTabs } from '@/utils/transform-tabs';
const {
    pattern,
    outputTopic,
    inOutTopics,
    loadingSetInOutTopics,
    topicTableDataLoading,
    setInOutError,
    setInOutResult,
    fetchInOutError,
    callSetInOutTopics,
    fetchInOutTopics,
    initializePatternData,
    navigateToTopic,
    createContentChangeHandler
} = useTopicPatternManager(props, emit);

// Local state - Editor loading
const editorLoading = ref(false);

// Template mapping
const templateMap = {
    COMMON_TRANSFORM: commonTransformTemplate,
    VALUE_TRANSFORM: valueTransformTemplate,
    KEY_TRANSFORM: keyTransformTemplate,
    TOPIC_TRANSFORM: topicTransformTemplate,
    VALUE_SCHEMA_TRANSFORM: valueSchemaTransformTemplate,
    KEY_SCHEMA_TRANSFORM: keySchemaTransformTemplate
};

// Use shared transform editor functionality
const {
    monacoTheme,
    activeTabId,
    isSaving,
    handleTabSave,
    handleContentChange,
    handleLoadTemplate,
    handleTabChange,
    closeEditor,
    initializeActiveTab
} = useTransformEditor(props, emit, {
    defaultTabId: 'VALUE_TRANSFORM',
    templateMap
});

// Create auto-save enabled content change handler - all logic is now internal to composable
const handleContentChangeWithAutoSave = createContentChangeHandler(
    handleContentChange,
    (tabId) => tabId.includes('TRANSFORM') || tabId.includes('VALUE') || tabId.includes('KEY')
);

// Computed tabs for MonacoTabbedEditor
const editorTabs = computed(() => {
    return createScriptTransformTabs(props.implementationCurrentVersion?.implementation, 'python');
});

// Initialize data from props
const initializePythonTransformData = () => {
    if (props.transform && props.implementationCurrentVersion) {
        initializePatternData();
    }
};

watch(
    () => props.transform,
    () => {
        initializePythonTransformData();
    },
    { immediate: true, deep: true }
);
</script>
